import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import { LatLngBounds } from 'leaflet';
import { useAppSelector, useAppDispatch } from '../mapSlice';
import { RootState } from '../../../store';
import { SCALE_FACTORS } from './exportTypes';
import { createFullPageTemplate, calculateGroundDimensions } from './paperMapTemplate';
import { setViewportBounds } from './exportSlice';
import './ExportRectangle.scss';

// Constants for coordinate conversion
const METERS_PER_DEGREE_LAT = 111320;

export default function ExportRectangle() {
    const map = useMap();
    const dispatch = useAppDispatch();
    const { pageSize, orientation, scale, showExportRect } = useAppSelector(
        (state: RootState) => state.export
    );

    // Track container size for centering
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    // Track zoom level to trigger recalculation of pixel size
    const [zoom, setZoom] = useState(map.getZoom());

    // Get the current template based on settings
    const template = useMemo(() => {
        return createFullPageTemplate(pageSize, orientation);
    }, [pageSize, orientation]);

    // Calculate the screen pixel dimensions for the rectangle
    const rectPixelSize = useMemo(() => {
        const scaleFactor = SCALE_FACTORS[scale];
        const groundDims = calculateGroundDimensions(template, scaleFactor);

        // Get current meters per pixel from map
        const center = map.getCenter();
        const metersPerPixel = getMetersPerPixel(map, center.lat);

        return {
            width: groundDims.widthMeters / metersPerPixel,
            height: groundDims.heightMeters / metersPerPixel,
        };
    }, [map, template, scale, zoom]); // Added zoom dependency

    // Update container size and zoom on mount and changes
    useEffect(() => {
        const container = map.getContainer();
        const updateSize = () => {
            setContainerSize({
                width: container.clientWidth,
                height: container.clientHeight,
            });
        };

        const updateZoom = () => {
            setZoom(map.getZoom());
        };

        updateSize();
        updateZoom();

        const resizeObserver = new ResizeObserver(updateSize);
        resizeObserver.observe(container);

        map.on('zoomend', updateZoom);

        return () => {
            resizeObserver.disconnect();
            map.off('zoomend', updateZoom);
        };
    }, [map]);

    // Calculate viewport bounds from the fixed rectangle position
    const calculateBoundsFromScreenRect = useCallback(() => {
        const containerRect = map.getContainer().getBoundingClientRect();
        const centerX = containerRect.width / 2;
        const centerY = containerRect.height / 2;

        // Get the corners of the rectangle in screen coordinates
        const halfWidth = rectPixelSize.width / 2;
        const halfHeight = rectPixelSize.height / 2;

        // Convert screen corners to lat/lng
        const topLeft = map.containerPointToLatLng([centerX - halfWidth, centerY - halfHeight]);
        const bottomRight = map.containerPointToLatLng([centerX + halfWidth, centerY + halfHeight]);

        return new LatLngBounds(
            [bottomRight.lat, topLeft.lng],  // Southwest
            [topLeft.lat, bottomRight.lng]   // Northeast
        );
    }, [map, rectPixelSize]);

    // Update Redux store with viewport bounds whenever map moves or settings change
    useEffect(() => {
        if (!showExportRect) return;

        const updateBounds = () => {
            const bounds = calculateBoundsFromScreenRect();
            dispatch(setViewportBounds({
                southWest: { lat: bounds.getSouthWest().lat, lng: bounds.getSouthWest().lng },
                northEast: { lat: bounds.getNorthEast().lat, lng: bounds.getNorthEast().lng },
            }));
        };

        // Update immediately
        updateBounds();

        // Update on map move/zoom
        map.on('moveend', updateBounds);
        map.on('zoomend', updateBounds);

        return () => {
            map.off('moveend', updateBounds);
            map.off('zoomend', updateBounds);
        };
    }, [map, showExportRect, calculateBoundsFromScreenRect, dispatch]);

    if (!showExportRect) {
        return null;
    }

    // Calculate position to center the rectangle
    const left = (containerSize.width - rectPixelSize.width) / 2;
    const top = (containerSize.height - rectPixelSize.height) / 2;

    return (
        <div
            className="export-rectangle-overlay"
            style={{
                left: `${left}px`,
                top: `${top}px`,
                width: `${rectPixelSize.width}px`,
                height: `${rectPixelSize.height}px`,
            }}
        />
    );
}

/**
 * Calculate meters per pixel at a given latitude for the current zoom level
 */
function getMetersPerPixel(map: L.Map, latitude: number): number {
    // Earth's circumference at equator in meters
    const earthCircumference = 40075016.686;
    // Leaflet uses 256px tiles
    const tileSize = 256;
    const zoom = map.getZoom();

    // Meters per pixel at equator for this zoom
    const metersPerPixelAtEquator = earthCircumference / (tileSize * Math.pow(2, zoom));

    // Adjust for latitude (Mercator projection)
    const metersPerPixel = metersPerPixelAtEquator * Math.cos(latitude * Math.PI / 180);

    return metersPerPixel;
}
